import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { useChatStore } from '../../store/useChatStore';
import { type CanvasThreadListItem } from './types';

const HTTP_STATUS_NOT_FOUND = 404;

/** Returns the first thread with no persisted messages, or undefined.
 *  Only considers threads already hydrated into the store — unvisited threads
 *  have no store entry and must not be mistaken for blank. */
function findBlankThreadInStore(threads: CanvasThreadListItem[]): CanvasThreadListItem | undefined {
  return threads.find((thread) => {
    const threadData = useChatStore.getState().threads[thread.id];

    return threadData !== undefined && threadData.messages.length === 0;
  });
}

/** Checks whether a thread is blank by fetching its S3 messagesUrl.
 *  Returns true if the S3 file does not exist (404). This is the slow-path
 *  fallback for threads not yet hydrated in the Zustand store. */
async function isThreadBlankRemote(params: {
  canvasId: string;
  threadId: string;
  workspaceId: string;
}): Promise<boolean> {
  const thread = await callAirApi(() =>
    Canvases.Threads.get({ canvasId: params.canvasId, id: params.threadId, workspaceId: params.workspaceId }),
  );

  if (!thread.messagesUrl) {
    return true;
  }

  const res = await fetch(thread.messagesUrl);

  return res.status === HTTP_STATUS_NOT_FOUND;
}

/** Finds a blank thread to reuse, checking both hydrated store entries (fast)
 *  and the latest thread's S3 state (slow fallback). */
export async function findBlankThread(
  threads: CanvasThreadListItem[],
  params: { canvasId: string; workspaceId: string },
): Promise<CanvasThreadListItem | undefined> {
  // Synchronous store lookup avoids a network round-trip for already-visited threads
  const hydrated = findBlankThreadInStore(threads);

  if (hydrated) {
    return hydrated;
  }

  // The latest thread is most likely to be blank (just created in another session).
  // Only check one thread to cap network cost at a single round-trip.
  const latestThread = threads[0];

  if (!latestThread) {
    return undefined;
  }

  const isLatestHydrated = useChatStore.getState().threads[latestThread.id] !== undefined;

  if (isLatestHydrated) {
    return undefined;
  }

  try {
    const isBlank = await isThreadBlankRemote({
      canvasId: params.canvasId,
      threadId: latestThread.id,
      workspaceId: params.workspaceId,
    });

    if (isBlank) {
      // Hydrate the store so subsequent checks take the fast path
      useChatStore.getState().setMessagesForThread(latestThread.id, []);
      return latestThread;
    }
  } catch {
    // Network error — fail open (allow thread creation)
  }

  return undefined;
}
